import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Outlet } from 'react-router-dom';
import { AuthProvider } from './components/auth/AuthProvider';
import ProtectedRoute from './components/auth/ProtectedRoute';
import MainLayout from './components/layout/MainLayout';
import ThemeManager from './components/layout/ThemeManager';

// Eagerly loaded Auth & Error Page fallbacks (for instant access)
import Login from './pages/auth/Login';
import Register from './pages/auth/Register';
import NotFound from './pages/error/404';
import ServerError from './pages/error/500';
import Forbidden from './pages/error/403';

// Lazy loaded page components
const Dashboard = lazy(() => import('./pages/dashboard/Dashboard'));
const DashboardAnalytics = lazy(() => import('./pages/dashboard/DashboardAnalytics'));
const DashboardCrypto = lazy(() => import('./pages/dashboard/DashboardCrypto'));
const DashboardHrms = lazy(() => import('./pages/dashboard/DashboardHrms'));
const DashboardJobs = lazy(() => import('./pages/dashboard/DashboardJobs'));
const ForgotPassword = lazy(() => import('./pages/auth/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/auth/ResetPassword'));
const TwoFactor = lazy(() => import('./pages/auth/TwoFactor'));
const LockScreen = lazy(() => import('./pages/auth/LockScreen'));
const Starter = lazy(() => import('./pages/Starter'));
const Users = lazy(() => import('./pages/Users'));

// E-commerce Pages
const ProductCatalog = lazy(() => import('./pages/apps/products/ProductCatalog'));
const ProductDetails = lazy(() => import('./pages/apps/products/ProductDetails'));
const ProductCreate = lazy(() => import('./pages/apps/products/ProductCreate'));
const ProductEdit = lazy(() => import('./pages/apps/products/ProductEdit'));
const ShoppingCart = lazy(() => import('./pages/apps/products/ShoppingCart'));
const Checkout = lazy(() => import('./pages/apps/products/Checkout'));
const Wishlist = lazy(() => import('./pages/apps/products/Wishlist'));
const OrdersList = lazy(() => import('./pages/apps/products/OrdersList'));
const OrderDetails = lazy(() => import('./pages/apps/products/OrderDetails'));
const TrackOrder = lazy(() => import('./pages/apps/products/TrackOrder'));
const RefundReturn = lazy(() => import('./pages/apps/products/RefundReturn'));

// HRMS Pages
const HrmsDashboard = lazy(() => import('./pages/apps/hrms/HrmsDashboard'));
const EmployeeDetails = lazy(() => import('./pages/apps/hrms/EmployeeDetails'));
const EmployeeCreate = lazy(() => import('./pages/apps/hrms/EmployeeCreate'));
const EmployeeEdit = lazy(() => import('./pages/apps/hrms/EmployeeEdit'));
const AttendanceTracker = lazy(() => import('./pages/apps/hrms/AttendanceTracker'));
const LeaveManager = lazy(() => import('./pages/apps/hrms/LeaveManager'));
const PayrollLedger = lazy(() => import('./pages/apps/hrms/PayrollLedger'));
const DepartmentList = lazy(() => import('./pages/apps/hrms/DepartmentList'));
const HolidayCalendar = lazy(() => import('./pages/apps/hrms/HolidayCalendar'));
const OnboardingChecklist = lazy(() => import('./pages/apps/hrms/OnboardingChecklist'));
const PerformanceReviews = lazy(() => import('./pages/apps/hrms/PerformanceReviews'));

// Job Portal Pages
const JobsDashboard = lazy(() => import('./pages/apps/jobs/JobsDashboard'));
const JobDetails = lazy(() => import('./pages/apps/jobs/JobDetails'));
const JobCreate = lazy(() => import('./pages/apps/jobs/JobCreate'));
const JobEdit = lazy(() => import('./pages/apps/jobs/JobEdit'));
const CandidateList = lazy(() => import('./pages/apps/jobs/CandidateList'));
const ApplicationTracker = lazy(() => import('./pages/apps/jobs/ApplicationTracker'));
const InterviewScheduler = lazy(() => import('./pages/apps/jobs/InterviewScheduler'));
const ReferralProgram = lazy(() => import('./pages/apps/jobs/ReferralProgram'));
const RecruiterAnalytics = lazy(() => import('./pages/apps/jobs/RecruiterAnalytics'));
const Messages = lazy(() => import('./pages/apps/messages/Messages'));
const Calendar = lazy(() => import('./pages/apps/calendar/Calendar'));
const Email = lazy(() => import('./pages/apps/email/Email'));
const EmailRead = lazy(() => import('./pages/apps/email/EmailRead'));
const Database = lazy(() => import('./pages/Database'));
const Settings = lazy(() => import('./pages/Settings'));
const UIComponents = lazy(() => import('./pages/UIComponents'));
const Profile = lazy(() => import('./pages/Profile'));
const TablePage = lazy(() => import('./pages/components/tables/table/TablePage'));
const Forms = lazy(() => import('./pages/components/forms/Forms'));

// Crypto App Pages
const CryptoMarket = lazy(() => import('./pages/apps/crypto/CryptoMarket'));
const CryptoTrade = lazy(() => import('./pages/apps/crypto/CryptoTrade'));
const CryptoStaking = lazy(() => import('./pages/apps/crypto/CryptoStaking'));
const CryptoTransactions = lazy(() => import('./pages/apps/crypto/CryptoTransactions'));

// Lazy loaded component sub-pages
const AccordionPage = lazy(() => import('./pages/components/advanced-ui/accordion/AccordionPage'));
const AlertPage = lazy(() => import('./pages/components/basic-ui/alert/AlertPage'));
const AvatarPage = lazy(() => import('./pages/components/basic-ui/avatar/AvatarPage'));
const BadgePage = lazy(() => import('./pages/components/basic-ui/badge/BadgePage'));
const ButtonPage = lazy(() => import('./pages/components/basic-ui/button/ButtonPage'));
const CheckboxPage = lazy(() => import('./pages/components/forms/checkbox/CheckboxPage'));
const SelectPage = lazy(() => import('./pages/components/forms/select/SelectPage'));
const SeparatorPage = lazy(() => import('./pages/components/basic-ui/separator/SeparatorPage'));
const SwitchPage = lazy(() => import('./pages/components/forms/switch/SwitchPage'));
const TabsPage = lazy(() => import('./pages/components/basic-ui/tabs/TabsPage'));
const SkeletonPage = lazy(() => import('./pages/components/basic-ui/skeleton/SkeletonPage'));
const ProgressPage = lazy(() => import('./pages/components/basic-ui/progress/ProgressPage'));
const SliderPage = lazy(() => import('./pages/components/basic-ui/slider/SliderPage'));
const DialogPage = lazy(() => import('./pages/components/basic-ui/dialog/DialogPage'));
const SheetPage = lazy(() => import('./pages/components/basic-ui/sheet/SheetPage'));
const PopoverPage = lazy(() => import('./pages/components/basic-ui/popover/PopoverPage'));
const TooltipPage = lazy(() => import('./pages/components/basic-ui/tooltip/TooltipPage'));
const RadioGroupPage = lazy(() => import('./pages/components/forms/radio-group/RadioGroupPage'));
const TextareaPage = lazy(() => import('./pages/components/forms/textarea/TextareaPage'));
const BreadcrumbPage = lazy(() => import('./pages/components/basic-ui/breadcrumb/BreadcrumbPage'));
const PaginationPage = lazy(() => import('./pages/components/basic-ui/pagination/PaginationPage'));
const AlertDialogPage = lazy(() => import('./pages/components/advanced-ui/alert-dialog/AlertDialogPage'));
const DropdownMenuPage = lazy(() => import('./pages/components/basic-ui/dropdown-menu/DropdownMenuPage'));
const AspectRatioPage = lazy(() => import('./pages/components/advanced-ui/aspect-ratio/AspectRatioPage'));
const CollapsiblePage = lazy(() => import('./pages/components/basic-ui/collapsible/CollapsiblePage'));
const TogglePage = lazy(() => import('./pages/components/basic-ui/toggle/TogglePage'));
const ComboboxPage = lazy(() => import('./pages/components/advanced-ui/combobox/ComboboxPage'));
const CommandPage = lazy(() => import('./pages/components/advanced-ui/command/CommandPage'));
const TypographyPage = lazy(() => import('./pages/components/basic-ui/typography/TypographyPage'));
const NavbarsPage = lazy(() => import('./pages/components/basic-ui/navbars/NavbarsPage'));
const ListPage = lazy(() => import('./pages/components/basic-ui/list/ListPage'));
const ListGroupPage = lazy(() => import('./pages/components/advanced-ui/list-group/ListGroupPage'));
const SpinnerPage = lazy(() => import('./pages/components/advanced-ui/spinner/SpinnerPage'));
const MediaObjectPage = lazy(() => import('./pages/components/advanced-ui/media-object/MediaObjectPage'));
const ToastPage = lazy(() => import('./pages/components/advanced-ui/toast/ToastPage'));
const SweetAlertPage = lazy(() => import('./pages/components/advanced-ui/sweet-alert/SweetAlertPage'));
const TourPage = lazy(() => import('./pages/components/advanced-ui/tour/TourPage'));
const SwiperPage = lazy(() => import('./pages/components/advanced-ui/swiper/SwiperPage'));
const RatingsPage = lazy(() => import('./pages/components/advanced-ui/ratings/RatingsPage'));
const BlockUIPage = lazy(() => import('./pages/components/advanced-ui/block-ui/BlockUIPage'));
const DragDropPage = lazy(() => import('./pages/components/advanced-ui/drag-drop/DragDropPage'));
const CounterUpPage = lazy(() => import('./pages/components/advanced-ui/counter-up/CounterUpPage'));
const TreePage = lazy(() => import('./pages/components/advanced-ui/tree/TreePage'));
const WatermarkPage = lazy(() => import('./pages/components/advanced-ui/watermark/WatermarkPage'));
const LinkPage = lazy(() => import('./pages/components/basic-ui/link/LinkPage'));
const RibbonsPage = lazy(() => import('./pages/components/basic-ui/ribbons/RibbonsPage'));
const UtilitiesPage = lazy(() => import('./pages/components/basic-ui/utilities/UtilitiesPage'));
const EmbeddedVideoPage = lazy(() => import('./pages/components/basic-ui/embedded-video/EmbeddedVideoPage'));
const LegendPage = lazy(() => import('./pages/components/basic-ui/legend/LegendPage'));
const InputPage = lazy(() => import('./pages/components/forms/input/InputPage'));
const InputGroupPage = lazy(() => import('./pages/components/forms/input-group/InputGroupPage'));
const InputOTPPage = lazy(() => import('./pages/components/forms/input-otp/InputOTPPage'));
const LabelPage = lazy(() => import('./pages/components/forms/label/LabelPage'));
const NativeSelectPage = lazy(() => import('./pages/components/forms/native-select/NativeSelectPage'));
const DatePickerPage = lazy(() => import('./pages/components/forms/date-picker/DatePickerPage'));
const ResizablePage = lazy(() => import('./pages/components/forms/resizable/ResizablePage'));
const GridJsPage = lazy(() => import('./pages/components/tables/grid-js/GridJsPage'));
const ChartsPage = lazy(() => import('./pages/components/charts/ChartsPage'));
const ChartJsPage = lazy(() => import('./pages/components/charts/chartjs/ChartJsPage'));
const EchartsPage = lazy(() => import('./pages/components/charts/echarts/EchartsPage'));
const MapsPage = lazy(() => import('./pages/components/maps/MapsPage'));
const LeafletMapsPage = lazy(() => import('./pages/components/maps/LeafletMapsPage'));
const VectorMapsPage = lazy(() => import('./pages/components/maps/VectorMapsPage'));
const GoogleMapsPage = lazy(() => import('./pages/components/maps/GoogleMapsPage'));
const IconsPage = lazy(() => import('./pages/components/icons/IconsPage'));
const LucideIconsPage = lazy(() => import('./pages/components/icons/LucideIconsPage'));
const RemixIconsPage = lazy(() => import('./pages/components/icons/RemixIconsPage'));
const FontAwesomePage = lazy(() => import('./pages/components/icons/FontAwesomePage'));

// Shared Layout Shell Wrapper
const AppLayout = () => {
  return (
    <ProtectedRoute>
      <MainLayout>
        <Suspense
          fallback={
            <div className="flex items-center justify-center p-12 min-h-[300px]">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          }
        >
          <Outlet />
        </Suspense>
      </MainLayout>
    </ProtectedRoute>
  );
};

function App() {
  return (
    <Router>
      <ThemeManager />
      <AuthProvider>
        <Suspense
          fallback={
            <div className="min-h-screen bg-background flex items-center justify-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
          }
        >
          <Routes>
            {/* Auth Routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/two-factor" element={<TwoFactor />} />
            <Route path="/lock-screen" element={<LockScreen />} />

            {/* Error Routes */}
            <Route path="/404" element={<NotFound />} />
            <Route path="/500" element={<ServerError />} />
            <Route path="/403" element={<Forbidden />} />

            {/* Protected Routes utilizing Shared Layout Outlet */}
            <Route element={<AppLayout />}>
              <Route path="/" element={<Dashboard />} />
              <Route path="/dashboard-analytics" element={<DashboardAnalytics />} />
              <Route path="/dashboard-crypto" element={<DashboardCrypto />} />
              <Route path="/dashboard-hrms" element={<DashboardHrms />} />
              <Route path="/dashboard-job" element={<DashboardJobs />} />
              <Route path="/users" element={<Users />} />
              
              {/* Crypto App Routes */}
              <Route path="/apps/crypto/market" element={<CryptoMarket />} />
              <Route path="/apps/crypto/trade" element={<CryptoTrade />} />
              <Route path="/apps/crypto/staking" element={<CryptoStaking />} />
              <Route path="/apps/crypto/transactions" element={<CryptoTransactions />} />

              {/* E-commerce Routes */}
              <Route path="/products" element={<ProductCatalog />} />
              <Route path="/orders" element={<OrdersList />} />
              <Route path="/apps/ecommerce/products" element={<ProductCatalog />} />
              <Route path="/apps/ecommerce/product/:id" element={<ProductDetails />} />
              <Route path="/apps/ecommerce/create" element={<ProductCreate />} />
              <Route path="/apps/ecommerce/edit/:id" element={<ProductEdit />} />
              <Route path="/apps/ecommerce/cart" element={<ShoppingCart />} />
              <Route path="/apps/ecommerce/checkout" element={<Checkout />} />
              <Route path="/apps/ecommerce/wishlist" element={<Wishlist />} />
              <Route path="/apps/ecommerce/orders" element={<OrdersList />} />
              <Route path="/apps/ecommerce/order/:id" element={<OrderDetails />} />
              <Route path="/apps/ecommerce/track-order/:id" element={<TrackOrder />} />
              <Route path="/apps/ecommerce/refund/:id" element={<RefundReturn />} />
              
              {/* HRMS Routes */}
              <Route path="/hrms" element={<HrmsDashboard />} />
              <Route path="/apps/hrms/dashboard" element={<HrmsDashboard />} />
              <Route path="/apps/hrms/employee/:id" element={<EmployeeDetails />} />
              <Route path="/apps/hrms/create" element={<EmployeeCreate />} />
              <Route path="/apps/hrms/edit/:id" element={<EmployeeEdit />} />
              <Route path="/apps/hrms/attendance" element={<AttendanceTracker />} />
              <Route path="/apps/hrms/leaves" element={<LeaveManager />} />
              <Route path="/apps/hrms/payroll" element={<PayrollLedger />} />
              <Route path="/apps/hrms/onboarding" element={<OnboardingChecklist />} />
              <Route path="/apps/hrms/departments" element={<DepartmentList />} />
              <Route path="/apps/hrms/calendar" element={<HolidayCalendar />} />
              <Route path="/apps/hrms/performance/:id" element={<PerformanceReviews />} />

              {/* Job Portal Routes */}
              <Route path="/jobs" element={<JobsDashboard />} />
              <Route path="/apps/jobs/dashboard" element={<JobsDashboard />} />
              <Route path="/apps/jobs/job/:id" element={<JobDetails />} />
              <Route path="/apps/jobs/create" element={<JobCreate />} />
              <Route path="/apps/jobs/edit/:id" element={<JobEdit />} />
              <Route path="/apps/jobs/candidates" element={<CandidateList />} />
              <Route path="/apps/jobs/tracker" element={<ApplicationTracker />} />
              <Route path="/apps/jobs/scheduler" element={<InterviewScheduler />} />
              <Route path="/apps/jobs/referrals" element={<ReferralProgram />} />
              <Route path="/apps/jobs/analytics" element={<RecruiterAnalytics />} />
              <Route path="/messages" element={<Messages />} />
              <Route path="/calendar" element={<Calendar />} />
              <Route path="/email" element={<Email />} />
              <Route path="/email/:id" element={<EmailRead />} />
              <Route path="/database" element={<Database />} />
              <Route path="/settings" element={<Settings />} />
              <Route path="/ui-components" element={<UIComponents />} />
              <Route path="/profile" element={<Profile />} />
              <Route path="/starter" element={<Starter />} />
              <Route path="/tables" element={<TablePage />} />
              <Route path="/forms" element={<Forms />} />

              {/* Component Pages */}
              <Route path="/components/accordion" element={<AccordionPage />} />
              <Route path="/components/alert" element={<AlertPage />} />
              <Route path="/components/button" element={<ButtonPage />} />
              <Route path="/components/badge" element={<BadgePage />} />
              <Route path="/components/avatar" element={<AvatarPage />} />
              <Route path="/components/select" element={<SelectPage />} />
              <Route path="/components/checkbox" element={<CheckboxPage />} />
              <Route path="/components/separator" element={<SeparatorPage />} />
              <Route path="/components/switch" element={<SwitchPage />} />
              <Route path="/components/tabs" element={<TabsPage />} />
              <Route path="/components/skeleton" element={<SkeletonPage />} />
              <Route path="/components/progress" element={<ProgressPage />} />
              <Route path="/components/slider" element={<SliderPage />} />
              <Route path="/components/dialog" element={<DialogPage />} />
              <Route path="/components/sheet" element={<SheetPage />} />
              <Route path="/components/popover" element={<PopoverPage />} />
              <Route path="/components/tooltip" element={<TooltipPage />} />
              <Route path="/components/radio-group" element={<RadioGroupPage />} />
              <Route path="/components/textarea" element={<TextareaPage />} />
              <Route path="/components/aspect-ratio" element={<AspectRatioPage />} />
              <Route path="/components/collapsible" element={<CollapsiblePage />} />
              <Route path="/components/toggle" element={<TogglePage />} />
              <Route path="/components/typography" element={<TypographyPage />} />
              <Route path="/components/breadcrumb" element={<BreadcrumbPage />} />
              <Route path="/components/pagination" element={<PaginationPage />} />
              <Route path="/components/combobox" element={<ComboboxPage />} />
              <Route path="/components/command" element={<CommandPage />} />
              <Route path="/components/input" element={<InputPage />} />
              <Route path="/components/input-group" element={<InputGroupPage />} />
              <Route path="/components/input-otp" element={<InputOTPPage />} />
              <Route path="/components/label" element={<LabelPage />} />
              <Route path="/components/native-select" element={<NativeSelectPage />} />
              <Route path="/components/date-picker" element={<DatePickerPage />} />
              <Route path="/components/resizable" element={<ResizablePage />} />
              <Route path="/components/alert-dialog" element={<AlertDialogPage />} />
              <Route path="/components/dropdown-menu" element={<DropdownMenuPage />} />
              <Route path="/components/navbars" element={<NavbarsPage />} />
              <Route path="/components/list" element={<ListPage />} />
              <Route path="/components/list-group" element={<ListGroupPage />} />
              <Route path="/components/spinner" element={<SpinnerPage />} />
              <Route path="/components/media-object" element={<MediaObjectPage />} />
              <Route path="/components/toast" element={<ToastPage />} />
              <Route path="/components/sweet-alert" element={<SweetAlertPage />} />
              <Route path="/components/tour" element={<TourPage />} />
              <Route path="/components/swiper" element={<SwiperPage />} />
              <Route path="/components/ratings" element={<RatingsPage />} />
              <Route path="/components/block-ui" element={<BlockUIPage />} />
              <Route path="/components/drag-drop" element={<DragDropPage />} />
              <Route path="/components/counter-up" element={<CounterUpPage />} />
              <Route path="/components/tree" element={<TreePage />} />
              <Route path="/components/watermark" element={<WatermarkPage />} />
              <Route path="/components/link" element={<LinkPage />} />
              <Route path="/components/ribbons" element={<RibbonsPage />} />
              <Route path="/components/utilities" element={<UtilitiesPage />} />
              <Route path="/components/embedded-video" element={<EmbeddedVideoPage />} />
              <Route path="/components/legend" element={<LegendPage />} />
              <Route path="/components/grid-js" element={<GridJsPage />} />
              <Route path="/components/charts" element={<ChartsPage />} />
              <Route path="/components/chartjs" element={<ChartJsPage />} />
              <Route path="/components/echarts" element={<EchartsPage />} />
              <Route path="/components/maps" element={<MapsPage />} />
              <Route path="/components/leaflet-maps" element={<LeafletMapsPage />} />
              <Route path="/components/vector-maps" element={<VectorMapsPage />} />
              <Route path="/components/google-maps" element={<GoogleMapsPage />} />
              <Route path="/components/icons" element={<IconsPage />} />
              <Route path="/components/lucide-icons" element={<LucideIconsPage />} />
              <Route path="/components/remix-icons" element={<RemixIconsPage />} />
              <Route path="/components/font-awesome" element={<FontAwesomePage />} />
            </Route>

            {/* Catch-all Fallback */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </AuthProvider>
    </Router>
  );
}

export default App;